import Link from "next/link";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Laptop,
  Spline,
  Target,
  UsersRound,
} from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import { BOOK_DEMO_HREF } from "@/lib/marketing-links";

/**
 * The enterprise journey from first conversation to measured impact. "Book a Consultation" leads as
 * the highlighted entry point; the five steps after it link to their sections on
 * /how-it-works (ids from `workflowSteps` in EnterpriseWorkflow.tsx).
 */
const journey = [
  {
    title: "Book a Consultation",
    body: "Talk to our team about your goals and needs.",
    Icon: CalendarDays,
    href: BOOK_DEMO_HREF,
  },
  {
    title: "Assess",
    body: "Identify skills gaps and role requirements.",
    Icon: Target,
    href: "/how-it-works#assess-skills",
  },
  {
    title: "Personalized Paths",
    body: "AI builds tailored learning journeys.",
    Icon: Spline,
    href: "/how-it-works#build-learning-paths",
  },
  {
    title: "Practice",
    body: "Engage in hands-on, role-based learning.",
    Icon: Laptop,
    href: "/how-it-works#learn-and-practice",
  },
  {
    title: "Human Validation",
    body: "Get expert review and certification.",
    Icon: UsersRound,
    href: "/how-it-works#human-expert-validation",
  },
  {
    title: "Measure Impact",
    body: "Track progress, skills gains and business outcomes.",
    Icon: BarChart3,
    href: "/how-it-works#measure-outcomes",
  },
] as const;

/** Reveal only ships these delay steps (see components/ui/Reveal.tsx). */
const STAGGER = [0, 100, 150, 200, 300, 300] as const;

/**
 * A light-blue panel: intro copy on top, then the six cards. Six cards and five arrows only
 * fit in one row from 2xl (1536px); this sits inside InaraXDifference's padded frame, and
 * below that width words like "Personalized" cannot shrink, so the row overflowed the panel.
 * The intro stays above the cards for the same reason: beside them it took ~300px the row
 * needs. Below 2xl the cards wrap into a grid and the arrows are dropped, since they would
 * point at the wrong neighbour once a row breaks.
 */
export default function EnterpriseJourney() {
  return (
    <div
      id="enterprise-journey"
      className="scroll-mt-24 rounded-3xl bg-[#edf3fe] border border-mkt-primary/10 p-size-md sm:p-size-lg flex flex-col gap-size-lg"
    >
      <Reveal className="max-w-2xl">
        <span className="font-label-md text-label-md uppercase tracking-widest text-mkt-primary">
          Enterprise journey
        </span>
        <h3 className="mt-size-sm font-headline-lg text-headline-sm md:text-headline-md font-bold text-mkt-inarax-black leading-snug">
          From skills gaps to measurable impact
        </h3>
        <p className="mt-size-sm font-body-md text-body-md text-mkt-on-surface-variant">
          A simple, proven path to build, validate and measure workforce skills,
          powered by AI and human expertise.
        </p>
      </Reveal>

      <ol className="list-none pl-0 ml-0 grid grid-cols-1 min-[480px]:grid-cols-2 md:grid-cols-3 2xl:flex 2xl:items-stretch gap-size-sm 2xl:gap-2">
        {journey.map(({ title, body, Icon, href }, index) => {
          const primary = index === 0;
          return (
            <li
              key={title}
              className="2xl:flex-1 2xl:flex 2xl:items-center 2xl:gap-2 min-w-0"
            >
              {index > 0 ? (
                <ArrowRight
                  aria-hidden="true"
                  className="hidden 2xl:block h-4 w-4 shrink-0 text-mkt-on-surface-variant"
                />
              ) : null}
              <Reveal
                delayMs={STAGGER[index]}
                className="h-full 2xl:flex-1 min-w-0"
              >
                <Link
                  href={href}
                  className={`group h-full flex flex-row items-start text-left gap-size-sm min-[480px]:flex-col min-[480px]:items-center min-[480px]:text-center rounded-xl px-size-md min-[480px]:px-size-sm py-size-sm min-[480px]:py-size-md transition-all duration-300 hover:-translate-y-1 ${
                    primary
                      ? "bg-mkt-primary-container text-mkt-on-primary shadow-[0_8px_24px_rgba(0,80,239,0.35)]"
                      : "bg-white text-mkt-on-surface border border-mkt-primary/10 shadow-[0_4px_16px_rgba(0,60,185,0.08)] hover:border-mkt-primary/40"
                  }`}
                >
                  <Icon
                    aria-hidden="true"
                    strokeWidth={1.75}
                    className={`h-7 w-7 min-[480px]:h-8 min-[480px]:w-8 shrink-0 ${primary ? "text-mkt-on-primary" : "text-mkt-primary-container"}`}
                  />
                  {/* Phones: icon beside the text, one compact row per step, so the six
                      steps don't stack into six tall centred cards. */}
                  <span className="flex min-w-0 flex-col gap-1 min-[480px]:items-center min-[480px]:gap-size-sm">
                    <span className="font-headline-md text-body-lg font-bold leading-tight break-words">
                      {title}
                    </span>
                    <span
                      className={`text-sm leading-snug ${primary ? "text-white" : "text-mkt-on-surface-variant"}`}
                    >
                      {body}
                    </span>
                  </span>
                </Link>
              </Reveal>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
